import { useState, type ReactNode } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { describeError, useResendVerification } from '@/features/auth/api';
import type { UserProfile } from '@/features/auth/session';
import { useCancelEmailChange, useChangeUserName, useRequestEmailChange } from '../api';
import { ProfileCard } from './ProfileCard';

type Editing = 'username' | 'email' | null;

/**
 * The account's own details, and the three things a member can change about
 * them.
 *
 * Each row collapses to a value and a "Değiştir"; only one form is open at a
 * time, because two half-filled forms in one card is how somebody submits the
 * wrong one.
 */
export function AccountInfoCard({ profile }: { profile: UserProfile }) {
  const [editing, setEditing] = useState<Editing>(null);

  return (
    <ProfileCard title="Hesap bilgileri">
      <dl className="flex flex-col divide-y divide-surface-800/70">
        <Row
          icon={<MailIcon />}
          label="E-posta"
          value={profile.email ? mask(profile.email) : '— (Telegram ile giriş)'}
          badge={
            profile.email
              ? profile.isEmailVerified
                ? { text: 'doğrulandı', tone: 'ok' }
                : { text: 'doğrulanmadı', tone: 'warn' }
              : undefined
          }
          action={
            profile.hasPassword ? (
              <EditButton
                open={editing === 'email'}
                onClick={() => setEditing(editing === 'email' ? null : 'email')}
              />
            ) : null
          }
        >
          {profile.pendingEmail ? <PendingEmailNotice profile={profile} /> : null}
          {profile.email && !profile.isEmailVerified ? (
            <ResendVerification email={profile.email} />
          ) : null}
          {editing === 'email' ? <EmailForm onDone={() => setEditing(null)} /> : null}
        </Row>

        <Row
          icon={<UserIcon />}
          label="Kullanıcı adı"
          value={profile.userName}
          action={
            <EditButton
              open={editing === 'username'}
              onClick={() => setEditing(editing === 'username' ? null : 'username')}
            />
          }
        >
          {editing === 'username' ? (
            <UserNameForm current={profile.userName} onDone={() => setEditing(null)} />
          ) : null}
        </Row>

        <Row
          icon={<CalendarIcon />}
          label="Üyelik başlangıcı"
          value={new Intl.DateTimeFormat('tr-TR', { dateStyle: 'long' }).format(
            new Date(profile.memberSinceUtc),
          )}
        />

        <Row
          icon={<TelegramIcon />}
          label="Telegram"
          value={
            profile.telegram
              ? profile.telegram.username
                ? `@${profile.telegram.username}`
                : String(profile.telegram.telegramUserId)
              : '— (bağlı değil)'
          }
          badge={
            profile.telegram
              ? profile.telegram.isGroupMember
                ? { text: 'grup üyesi', tone: 'ok' }
                : { text: 'grupta değil', tone: 'warn' }
              : undefined
          }
        />
      </dl>
    </ProfileCard>
  );
}

/**
 * Shows enough of the address to recognise and not enough to hand over.
 *
 * The profile is behind a login, so this is not a security control — it is for
 * the screenshot the member posts in the community chat without thinking.
 */
function mask(email: string): string {
  const at = email.lastIndexOf('@');

  if (at <= 0) {
    return email;
  }

  const name = email.slice(0, at);
  const visible = name.slice(0, Math.min(2, name.length));

  return `${visible}${'•'.repeat(Math.max(3, name.length - visible.length))}${email.slice(at)}`;
}

interface RowProps {
  icon: ReactNode;
  label: string;
  value: string;
  badge?: { text: string; tone: 'ok' | 'warn' } | undefined;
  action?: ReactNode;
  children?: ReactNode;
}

function Row({ icon, label, value, badge, action, children }: RowProps) {
  return (
    <div className="py-3.5 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <span aria-hidden className="mt-0.5 shrink-0 text-surface-600">
          {icon}
        </span>

        <div className="min-w-0 flex-1">
          <dt className="text-xs text-surface-500">{label}</dt>
          <dd className="mt-0.5 flex flex-wrap items-center gap-2">
            <span className="truncate text-sm text-surface-100">{value}</span>
            {badge ? (
              <span
                className={
                  badge.tone === 'ok'
                    ? 'rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-300'
                    : 'rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-300'
                }
              >
                {badge.text}
              </span>
            ) : null}
          </dd>
        </div>

        {action}
      </div>

      {children ? <div className="mt-3 pl-8">{children}</div> : null}
    </div>
  );
}

function EditButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className="shrink-0 text-sm text-brand-400 transition hover:text-brand-300"
    >
      {open ? 'Vazgeç' : 'Değiştir'}
    </button>
  );
}

// -----------------------------------------------------------------------------

function UserNameForm({ current, onDone }: { current: string; onDone: () => void }) {
  const [userName, setUserName] = useState(current);
  const change = useChangeUserName();

  const trimmed = userName.trim();

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();

        if (trimmed === current) {
          return;
        }

        change.mutate(trimmed, { onSuccess: onDone });
      }}
    >
      {change.isError ? <Alert tone="error">{describeError(change.error)}</Alert> : null}

      <Field
        label="Yeni kullanıcı adı"
        value={userName}
        autoComplete="username"
        maxLength={32}
        hint="3–32 karakter. Harf, rakam ve alt çizgi."
        onChange={(event) => setUserName(event.target.value)}
      />

      <Button
        type="submit"
        loading={change.isPending}
        disabled={trimmed === current || trimmed === ''}
      >
        Kaydet
      </Button>
    </form>
  );
}

function EmailForm({ onDone }: { onDone: () => void }) {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const request = useRequestEmailChange();

  if (request.isSuccess) {
    return <Alert tone="success">{request.data.message}</Alert>;
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        request.mutate({ newEmail: newEmail.trim(), currentPassword });
      }}
    >
      {request.isError ? <Alert tone="error">{describeError(request.error)}</Alert> : null}

      <Field
        label="Yeni e-posta"
        type="email"
        value={newEmail}
        autoComplete="email"
        placeholder="yeni@eposta.com"
        onChange={(event) => setNewEmail(event.target.value)}
      />

      <Field
        label="Mevcut parolan"
        type="password"
        value={currentPassword}
        autoComplete="current-password"
        hint="Adresi değiştiren kişinin sen olduğunu doğrulamak için."
        onChange={(event) => setCurrentPassword(event.target.value)}
      />

      <p className="text-xs text-surface-500">
        Onay bağlantısını <strong>yeni adrese</strong> göndeririz. Sen o bağlantıya tıklayana kadar
        mevcut adresinle giriş yapmaya devam edersin — yani yanlış yazsan bile hesabına erişimini
        kaybetmezsin.
      </p>

      <div className="flex gap-2">
        <Button
          type="submit"
          loading={request.isPending}
          disabled={newEmail.trim() === '' || currentPassword === ''}
        >
          Onay bağlantısı gönder
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Vazgeç
        </Button>
      </div>
    </form>
  );
}

function PendingEmailNotice({ profile }: { profile: UserProfile }) {
  const cancel = useCancelEmailChange();

  return (
    <Alert tone="info" className="flex flex-wrap items-center justify-between gap-3">
      <span>
        <strong>{profile.pendingEmail}</strong> adresine onay bağlantısı gönderildi.
      </span>

      <button
        type="button"
        onClick={() => cancel.mutate(undefined)}
        disabled={cancel.isPending}
        className="shrink-0 text-xs underline transition hover:no-underline disabled:opacity-50"
      >
        İptal et
      </button>
    </Alert>
  );
}

function ResendVerification({ email }: { email: string }) {
  const resend = useResendVerification();

  if (resend.isSuccess) {
    return <Alert tone="success">Doğrulama bağlantısı yeniden gönderildi.</Alert>;
  }

  return (
    <button
      type="button"
      onClick={() => resend.mutate(email)}
      disabled={resend.isPending}
      className="text-sm text-brand-400 transition hover:text-brand-300 disabled:opacity-50"
    >
      Doğrulama e-postasını yeniden gönder
    </button>
  );
}

// -----------------------------------------------------------------------------

function MailIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="size-4 fill-none stroke-current"
      strokeWidth={1.7}
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="size-4 fill-none stroke-current"
      strokeWidth={1.7}
    >
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3 3.1-5 7-5s7 2 7 5" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="size-4 fill-none stroke-current"
      strokeWidth={1.7}
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
      <path d="M21.9 4.3 18.7 19.4c-.2 1-.9 1.3-1.7.8l-4.7-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.8 8.7-7.9c.4-.3-.1-.5-.6-.2L6.7 13.2 2 11.7c-1-.3-1-1 .2-1.5l18.4-7.1c.9-.3 1.6.2 1.3 1.2z" />
    </svg>
  );
}
